/**
 * DataTable column definitions for Team Admin → Identity Providers.
 *
 * Split out of `identity-providers.tsx` for the 300-line file ceiling. Pure
 * presentation: protocol badge, federation status (including the "certificate
 * missing" / "no token" states a half-configured provider lands in), the
 * FR-AUTH-02 persona allow list and the realm / last-used status column.
 */
import type { DataTableColumn } from '../../components/data-table';
import type { RowMenuItem } from '../../components/row-menu';
import { StatusPill } from '../../components/status-pill';
import { PERSONA_LABELS } from '../../lib/personas';

import { PROTOCOL_LABELS, type IdentityProviderView } from './use-identity-providers';

export const PROTOCOL_FILTER_OPTIONS = [
  { value: 'all', label: 'All protocols' },
  { value: 'saml2', label: PROTOCOL_LABELS.saml2 },
  { value: 'scim2', label: PROTOCOL_LABELS.scim2 },
];

export const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'enabled', label: 'Active only' },
  { value: 'disabled', label: 'Inactive only' },
];

export interface ProviderRowHandlers {
  onEditPersonas: (provider: IdentityProviderView) => void;
  onRotate: (provider: IdentityProviderView) => void;
  onToggleEnabled: (provider: IdentityProviderView) => void;
  onDelete: (provider: IdentityProviderView) => void;
}

/** Kebab items. Edit is the row's primary action; "Rotate token" is SCIM-only. */
export function providerMenuItems(
  provider: IdentityProviderView,
  handlers: ProviderRowHandlers
): RowMenuItem[] {
  const items: RowMenuItem[] = [
    {
      id: 'personas',
      label: 'Allowed personas',
      onSelect: () => handlers.onEditPersonas(provider),
    },
  ];
  if (provider.protocol === 'scim2') {
    items.push({
      id: 'rotate',
      label: 'Rotate token',
      onSelect: () => handlers.onRotate(provider),
    });
  }
  items.push(
    {
      id: 'toggle',
      label: provider.is_enabled ? 'Disable' : 'Enable',
      onSelect: () => handlers.onToggleEnabled(provider),
    },
    { id: 'delete', label: 'Delete', tone: 'danger', onSelect: () => handlers.onDelete(provider) }
  );
  return items;
}

export function formatTimestamp(value: string | null): string {
  if (value === null) return 'Never';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Never' : parsed.toLocaleString();
}

export function buildProviderColumns(): DataTableColumn<IdentityProviderView>[] {
  return [
    {
      id: 'display_name',
      header: 'Provider',
      isSortable: true,
      isPinned: true,
      minWidth: 220,
      maxWidth: 420,
      grow: 3,
      render: (provider) => (
        <span className="user-cell" title={provider.saml_idp_entity_id ?? provider.slug}>
          <span className="user-cell__email">{provider.display_name}</span>
          <span className="muted">{provider.saml_idp_entity_id ?? provider.slug}</span>
        </span>
      ),
      renderDetail: (provider) =>
        `${provider.display_name} (${provider.saml_idp_entity_id ?? provider.slug})`,
    },
    {
      id: 'protocol',
      header: 'Protocol',
      isSortable: true,
      priority: 2,
      minWidth: 110,
      maxWidth: 130,
      grow: 0,
      render: (provider) => (
        <StatusPill tone="info">
          {PROTOCOL_LABELS[provider.protocol] ?? String(provider.protocol).toUpperCase()}
        </StatusPill>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      isSortable: true,
      priority: 1,
      minWidth: 210,
      maxWidth: 260,
      grow: 0,
      render: (provider) => (
        <span className="badge-row stat-cell__pills">
          <StatusPill tone={provider.is_enabled ? 'success' : 'neutral'}>
            {provider.is_enabled ? 'Active' : 'Inactive'}
          </StatusPill>
          {provider.protocol === 'saml2' && !provider.has_certificate ? (
            <StatusPill tone="warning">Certificate missing</StatusPill>
          ) : null}
          {provider.protocol === 'scim2' && !provider.has_scim_token ? (
            <StatusPill tone="warning">No token</StatusPill>
          ) : null}
        </span>
      ),
    },
    {
      id: 'allowed_personas',
      header: 'Allowed personas',
      priority: 3,
      minWidth: 200,
      grow: 2,
      render: (provider) =>
        provider.allowed_personas.length === 0 ? (
          <span className="muted">None — nobody can sign in</span>
        ) : (
          <span
            className="federation__personas"
            title={provider.allowed_personas.map((persona) => PERSONA_LABELS[persona]).join(', ')}
          >
            {provider.allowed_personas.map((persona) => PERSONA_LABELS[persona]).join(', ')}
          </span>
        ),
    },
    {
      id: 'last_used_at',
      header: 'Last used',
      isSortable: true,
      priority: 4,
      minWidth: 196,
      maxWidth: 240,
      grow: 0,
      render: (provider) => (
        <span className="user-cell">
          <span>{formatTimestamp(provider.last_used_at)}</span>
          <span className="muted">
            {provider.keycloak_realm_name === null
              ? 'Realm not provisioned'
              : `Realm ${provider.keycloak_realm_name}`}
          </span>
        </span>
      ),
    },
  ];
}
